import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { remoteLocalStorage } from '@wellesley/host-remote'

export const useRemoteLocalStorage = <T>({
  scope,
  key,
  defaultValue,
}: {
  scope?: string
  key: string
  defaultValue: T
}) => {
  const storage = useMemo(() => remoteLocalStorage(scope), [scope])
  const defaultValueRef = useRef(defaultValue)
  defaultValueRef.current = defaultValue

  const [value, setValueState] = useState(() => defaultValue)
  const [isLoading, setIsLoading] = useState(true)

  const refresh = useCallback(async () => {
    setIsLoading(true)
    try {
      const stored = await storage.getJSON<T>(key)
      setValueState(stored ?? defaultValueRef.current)
    } finally {
      setIsLoading(false)
    }
  }, [key, storage])

  useEffect(() => {
    void refresh()
    const unsubscribe = storage.subscribe(() => {
      void refresh()
    })
    return () => unsubscribe()
  }, [refresh, storage])

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValueState((prev) => {
        const resolved =
          typeof next === 'function' ? (next as (prev: T) => T)(prev) : next
        void storage.setJSON(key, resolved)
        return resolved
      })
    },
    [key, storage],
  )

  const remove = useCallback(() => {
    setValueState(defaultValueRef.current)
    void storage.removeItem(key)
  }, [key, storage])

  const clear = useCallback(() => {
    setValueState(defaultValueRef.current)
    void storage.clear()
  }, [storage])

  return {
    value,
    setValue,
    remove,
    clear,
    refresh,
    isLoading,
  } as const
}
